import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  KeyIcon,
  CheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { smsAPI } from '../../../services/api';

const Permissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [rolePermissions, setRolePermissions] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesResponse, permissionsResponse] = await Promise.all([
        smsAPI.getRoles(),
        smsAPI.getPermissionsGrouped(),
      ]);
      
      const rolesData = rolesResponse.data || [];
      const permissionsData = permissionsResponse.data || [];
      
      setRoles(rolesData);
      setPermissions(permissionsData);

      // Fetch permissions for each role
      const rolePermsMap = {};
      for (const role of rolesData) {
        const roleDetailsResponse = await smsAPI.getRoleById(role.id);
        const roleDetails = roleDetailsResponse.data;
        rolePermsMap[role.id] = new Set(roleDetails.permissions?.map(p => p.id) || []);
      }
      setRolePermissions(rolePermsMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setRoles([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (module) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(module)) {
        newSet.delete(module);
      } else {
        newSet.add(module);
      }
      return newSet;
    });
  };

  const togglePermission = (roleId, permissionId) => {
    setRolePermissions(prev => {
      const newMap = { ...prev };
      const rolePerms = new Set(newMap[roleId] || []);
      
      if (rolePerms.has(permissionId)) {
        rolePerms.delete(permissionId);
      } else {
        rolePerms.add(permissionId);
      }
      
      newMap[roleId] = rolePerms;
      return newMap;
    });
  };

  const toggleModuleForRole = (roleId, modulePermissions) => {
    const modulePermissionIds = modulePermissions.map(p => p.id);
    const rolePerms = rolePermissions[roleId] || new Set();
    const allSelected = modulePermissionIds.every(id => rolePerms.has(id));

    setRolePermissions(prev => {
      const newMap = { ...prev };
      const newRolePerms = new Set(newMap[roleId] || []);
      
      if (allSelected) {
        modulePermissionIds.forEach(id => newRolePerms.delete(id));
      } else {
        modulePermissionIds.forEach(id => newRolePerms.add(id));
      }
      
      newMap[roleId] = newRolePerms;
      return newMap;
    });
  };

  const saveRolePermissions = async (roleId) => {
    try {
      setSaving(true);
      const permissionIds = Array.from(rolePermissions[roleId] || []);
      await smsAPI.updateRole(roleId, { permission_ids: permissionIds });
      alert('Permissions updated successfully!');
    } catch (error) {
      console.error('Failed to save permissions:', error);
      alert(error.response?.data?.error || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionCount = (roleId) => {
    return (rolePermissions[roleId] || new Set()).size;
  };

  const getModulePermissionCount = (roleId, modulePermissions) => {
    const rolePerms = rolePermissions[roleId] || new Set();
    return modulePermissions.filter(p => rolePerms.has(p.id)).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F46E5]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">Permissions Builder</h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Manage permissions for each role using the matrix below
        </p>
      </div>

      {/* Permission Matrix */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        {/* Roles Header */}
        <div className="grid grid-cols-12 gap-4 p-4 bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0 z-10">
          <div className="col-span-4 font-medium text-[#111827]">
            Permissions
          </div>
          {roles.map((role) => (
            <div key={role.id} className="col-span-2 text-center">
              <div className="font-medium text-[#111827] text-sm mb-1">
                {role.role_name}
              </div>
              <div className="text-xs text-[#6B7280]">
                {getPermissionCount(role.id)} perms
              </div>
              {!role.is_system_role && (
                <button
                  onClick={() => saveRolePermissions(role.id)}
                  disabled={saving}
                  className="mt-2 w-full px-2 py-1 text-xs bg-[#4F46E5] text-white rounded hover:bg-[#4338CA] transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Permission Modules */}
        <div className="divide-y divide-[#E5E7EB]">
          {permissions.map((group) => (
            <div key={group.module}>
              {/* Module Header */}
              <div
                className="grid grid-cols-12 gap-4 p-4 hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                onClick={() => toggleModule(group.module)}
              >
                <div className="col-span-4 flex items-center gap-2">
                  <button className="p-1 hover:bg-[#E5E7EB] rounded transition-colors">
                    {expandedModules.has(group.module) ? (
                      <ChevronUpIcon className="w-4 h-4 text-[#6B7280]" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-[#6B7280]" />
                    )}
                  </button>
                  <KeyIcon className="w-5 h-5 text-[#4F46E5]" />
                  <span className="font-medium text-[#111827] capitalize">
                    {group.module}
                  </span>
                  <span className="text-xs text-[#6B7280]">
                    ({group.permissions.length})
                  </span>
                </div>
                {roles.map((role) => (
                  <div key={role.id} className="col-span-2 flex items-center justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!role.is_system_role) {
                          toggleModuleForRole(role.id, group.permissions);
                        }
                      }}
                      disabled={role.is_system_role}
                      className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
                        getModulePermissionCount(role.id, group.permissions) === group.permissions.length
                          ? 'bg-[#4F46E5] text-white'
                          : getModulePermissionCount(role.id, group.permissions) > 0
                          ? 'bg-[#EEF2FF] text-[#4F46E5]'
                          : 'bg-[#F3F4F6] text-[#9CA3AF]'
                      } ${!role.is_system_role ? 'hover:bg-[#4338CA] hover:text-white cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                    >
                      {getModulePermissionCount(role.id, group.permissions) === group.permissions.length ? (
                        <CheckIcon className="w-5 h-5" />
                      ) : getModulePermissionCount(role.id, group.permissions) > 0 ? (
                        <span className="text-xs font-bold">
                          {getModulePermissionCount(role.id, group.permissions)}
                        </span>
                      ) : (
                        <XMarkIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Individual Permissions */}
              {expandedModules.has(group.module) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-[#F9FAFB]"
                >
                  {group.permissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="grid grid-cols-12 gap-4 p-4 pl-12 hover:bg-[#F3F4F6] transition-colors"
                    >
                      <div className="col-span-4 flex items-center gap-2">
                        <span className="text-sm text-[#374151]">
                          {permission.permission_name}
                        </span>
                        <span className="px-2 py-0.5 text-xs bg-[#E5E7EB] text-[#6B7280] rounded">
                          {permission.action}
                        </span>
                      </div>
                      {roles.map((role) => (
                        <div key={role.id} className="col-span-2 flex items-center justify-center">
                          <button
                            onClick={() => {
                              if (!role.is_system_role) {
                                togglePermission(role.id, permission.id);
                              }
                            }}
                            disabled={role.is_system_role}
                            className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
                              (rolePermissions[role.id] || new Set()).has(permission.id)
                                ? 'bg-[#059669] text-white'
                                : 'bg-[#F3F4F6] text-[#9CA3AF]'
                            } ${!role.is_system_role ? 'hover:bg-[#047857] hover:text-white cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                          >
                            {(rolePermissions[role.id] || new Set()).has(permission.id) ? (
                              <CheckIcon className="w-5 h-5" />
                            ) : (
                              <XMarkIcon className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]">
          <div className="text-sm text-[#6B7280] mb-1">Total Roles</div>
          <div className="text-2xl font-bold text-[#111827]">{roles.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]">
          <div className="text-sm text-[#6B7280] mb-1">Total Permissions</div>
          <div className="text-2xl font-bold text-[#111827]">
            {permissions.reduce((sum, group) => sum + group.permissions.length, 0)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]">
          <div className="text-sm text-[#6B7280] mb-1">Permission Modules</div>
          <div className="text-2xl font-bold text-[#111827]">{permissions.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]">
          <div className="text-sm text-[#6B7280] mb-1">System Roles</div>
          <div className="text-2xl font-bold text-[#111827]">
            {roles.filter(r => r.is_system_role).length}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 bg-[#EEF2FF] rounded-lg p-4">
        <h3 className="font-medium text-[#111827] mb-3">Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#4F46E5] rounded flex items-center justify-center">
              <CheckIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-[#374151]">All module permissions</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#059669] rounded flex items-center justify-center">
              <CheckIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-[#374151]">Permission granted</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#EEF2FF] rounded flex items-center justify-center text-[#4F46E5] font-bold text-xs">
              3
            </div>
            <span className="text-sm text-[#374151]">Partial permissions</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#F3F4F6] rounded flex items-center justify-center">
              <XMarkIcon className="w-5 h-5 text-[#9CA3AF]" />
            </div>
            <span className="text-sm text-[#374151]">No permissions</span>
          </div>
        </div>
        <div className="mt-3 text-xs text-[#6B7280]">
          Note: System roles cannot be modified. Click on checkboxes to toggle permissions for custom roles.
        </div>
      </div>
    </div>
  );
};

export default Permissions;
